import {useTranslation} from 'react-i18next';
import {VERIFICATION_METHODS} from '@guestapp/sdk';
import {useMemo, useState, useEffect} from 'react';
import {useForm, FormProvider} from 'react-hook-form';
import {useDefaultContactData} from 'hooks/iv/useDefaultContactData';
import {FormTypes, VerificationData} from './types';
import {VerificationStep} from './constants';
import {EmailVerification} from './EmailVerification';
import {PhoneVerification} from './PhoneVerification';

type Props = {
  enableEmail?: boolean;
  enablePhone?: boolean;
  onVerificationComplete?: (data: VerificationData) => void;
};

const ContactsVerificationComplete = ({
  enableEmail = true,
  enablePhone = true,
  onVerificationComplete,
}: Props) => {
  const {t} = useTranslation();
  const {email: defaultEmail, phone: defaultPhone} = useDefaultContactData();

  const [currentStep, setCurrentStep] = useState<VerificationStep>(
    VerificationStep.CONTACTS_INPUT,
  );
  const [channelIndex, setChannelIndex] = useState<number>(0);
  const [completedMethods, setCompletedMethods] = useState<Set<string>>(new Set());

  const methods = useForm<FormTypes>({
    mode: 'onChange',
    defaultValues: {
      email: defaultEmail,
      phone: defaultPhone,
    },
  });
  const {setValue} = methods;

  useEffect(() => {
    setValue('phone', defaultPhone);
  }, [defaultPhone, setValue]);

  const enabledChannels = useMemo(
    () => [
      ...(enableEmail ? ([VERIFICATION_METHODS.email] as const) : []),
      ...(enablePhone ? ([VERIFICATION_METHODS.phone] as const) : []),
    ],
    [enableEmail, enablePhone],
  );

  const isEmailStep = enabledChannels[channelIndex] === VERIFICATION_METHODS.email;
  const isPhoneStep = enabledChannels[channelIndex] === VERIFICATION_METHODS.phone;
  const needsBothMethods = enableEmail && enablePhone;

  useEffect(() => {
    if (enabledChannels.length > 0) {
      setChannelIndex(0);
    }
  }, [enabledChannels]);

  const getStepIndicator = () => {
    if (!needsBothMethods) return '';

    if (completedMethods.size === 0) {
      return t('step_of', [1, 2]);
    } else if (completedMethods.size === 1) {
      return t('step_of', [2, 2]);
    }
    return '';
  };

  const handleOnVerify = async ({type, data, verified}: VerificationData) => {
    const isLastChannel = channelIndex >= enabledChannels.length - 1;
    const newCompletedMethods = new Set(completedMethods);
    newCompletedMethods.add(type);
    setCompletedMethods(newCompletedMethods);
    if (!isLastChannel) {
      setCurrentStep(VerificationStep.CONTACTS_INPUT);
      setChannelIndex(prev => prev + 1);
    }

    onVerificationComplete?.({type, data, verified});
  };

  const handleOnGenerate = () => {
    setCurrentStep(VerificationStep.CODES_VERIFICATION);
  };

  const handleChangeContacts = () => {
    setCurrentStep(VerificationStep.CONTACTS_INPUT);
  };

  return (
    <FormProvider {...methods}>
      {needsBothMethods && <div className="step-indicator">{getStepIndicator()}</div>}

      {isEmailStep && (
        <EmailVerification
          currentStep={currentStep}
          onChangeContacts={handleChangeContacts}
          onVerify={(data, verified) =>
            handleOnVerify({type: VERIFICATION_METHODS.email, data, verified})
          }
          onGenerate={handleOnGenerate}
        />
      )}

      {isPhoneStep && (
        <PhoneVerification
          currentStep={currentStep}
          onChangeContacts={handleChangeContacts}
          onVerify={(data, verified) =>
            handleOnVerify({type: VERIFICATION_METHODS.phone, data, verified})
          }
          onGenerate={handleOnGenerate}
        />
      )}
    </FormProvider>
  );
};

export {ContactsVerificationComplete};
